import { DocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import FormInputBooleanCheckbox from "@repo/components/src/forms/booleanCheckbox";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import TaxProfileFields from "@repo/components/src/shared/montu/fiscal/taxProfileFields/TaxProfileFields";
import UnsupportedState from "@repo/components/src/shared/montu/fiscal/taxProfileFields/UnsupportedState";
import { AcknowledgmentRule } from "@repo/components/src/shared/montu/fiscal/taxProfileFields/schema";
import { TaxProfileFormValues } from "@repo/components/src/shared/montu/fiscal/taxProfileFields/types";
import { useTaxProfileForm } from "@repo/components/src/shared/montu/fiscal/taxProfileFields/useTaxProfileForm";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { VStack } from "@repo/components/src/ui/vstack";
import { TaxProfileFragmentFragment } from "@repo/graphql/documents/fiscal/fragment.generated";
import {
  UpsertBrandTaxProfileDocument,
  UpsertWorkspaceTaxProfileDocument,
} from "@repo/graphql/documents/fiscal/mutation.generated";
import {
  WorkspaceBillingSettingsDocument,
  WorkspaceContextDocument,
} from "@repo/graphql/documents/workspace/query.generated";
import { TaxProfileOwnerKindEnum } from "@repo/graphql/gql/graphql";
import React from "react";

import { TaxProfileFormOwner } from "./types";

const SAVE_ERROR_MESSAGE = "No pudimos guardar tus datos. Inténtalo de nuevo.";
const ACKNOWLEDGMENT_COPY =
  "Confirmo que estos datos son correctos para los comprobantes que emita Montu.";

const ACKNOWLEDGMENT_RULE: AcknowledgmentRule = {
  kind: "always",
  message: "Confirma que estos datos son correctos",
};
const NO_ACKNOWLEDGMENT_RULE: AcknowledgmentRule = { kind: "none" };

function showErrorToast(message: string) {
  MontuToastProvider.show({
    placement: "bottom",
    duration: 3000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription>{message}</ToastDescription>
      </Toast>
    ),
  });
}

type Props = {
  owner: TaxProfileFormOwner;
  countryCode: string;
  countryName: string;
  initial?: TaxProfileFragmentFragment | null;
  defaultLegalName: string;
  defaultAddress?: string;
  defaultTaxId?: string;
  onSaved?: (profile: TaxProfileFragmentFragment) => void;
  submitLabel?: string;
  acknowledgment?: boolean;
  refetchQueries?: DocumentNode[];
};

export default function TaxProfileForm({
  owner,
  countryCode,
  countryName,
  initial,
  defaultLegalName,
  defaultAddress,
  defaultTaxId,
  onSaved,
  submitLabel = "Guardar",
  acknowledgment = false,
  refetchQueries = [],
}: Props) {
  const ownerKind =
    owner.kind === "WORKSPACE"
      ? TaxProfileOwnerKindEnum.Workspace
      : TaxProfileOwnerKindEnum.Brand;

  const acknowledgmentRule = acknowledgment
    ? ACKNOWLEDGMENT_RULE
    : NO_ACKNOWLEDGMENT_RULE;
  const form = useTaxProfileForm({
    countryCode,
    ownerKind,
    initial,
    defaultLegalName,
    defaultAddressLine: defaultAddress,
    defaultTaxId,
    acknowledgment: acknowledgmentRule,
  });
  const { formProps } = form;

  const mutationOptions = {
    onError: () => showErrorToast(SAVE_ERROR_MESSAGE),
    refetchQueries: [
      WorkspaceContextDocument,
      WorkspaceBillingSettingsDocument,
      ...refetchQueries,
    ],
  };
  const [upsertWorkspaceTaxProfile, { loading: submittingWorkspace }] =
    useMutation(UpsertWorkspaceTaxProfileDocument, mutationOptions);
  const [upsertBrandTaxProfile, { loading: submittingBrand }] = useMutation(
    UpsertBrandTaxProfileDocument,
    mutationOptions,
  );
  const submitting = submittingWorkspace || submittingBrand;

  const onSubmit = async (data: TaxProfileFormValues) => {
    try {
      const taxProfileInput = form.buildInput(data);
      const payload =
        owner.kind === "WORKSPACE"
          ? (
              await upsertWorkspaceTaxProfile({
                variables: { taxProfileInput },
              })
            ).data?.fiscal.upsertWorkspaceTaxProfile
          : (
              await upsertBrandTaxProfile({
                variables: { brandId: owner.brandId, taxProfileInput },
              })
            ).data?.fiscal.upsertBrandTaxProfile;
      const userErrors = payload?.userErrors;
      if (userErrors && userErrors.length > 0) {
        form.applyUserErrors(userErrors).forEach(showErrorToast);
        return;
      }
      const profile = payload?.taxProfile;
      if (!profile) {
        showErrorToast(SAVE_ERROR_MESSAGE);
        return;
      }
      MontuToastProvider.show({
        placement: "bottom",
        duration: 3000,
        render: ({ id }) => (
          <Toast nativeID={`toast-${id}`} action="success" variant="solid">
            <ToastDescription>
              Tus datos de facturación se guardaron correctamente.
            </ToastDescription>
          </Toast>
        ),
      });
      onSaved?.(profile);
    } catch {
      showErrorToast(SAVE_ERROR_MESSAGE);
    }
  };

  const handleSubmit = async () => {
    const addressValid = await form.validateAddress();
    await formProps.handleSubmit(async (data) => {
      if (addressValid) {
        await onSubmit(data);
      }
    })();
  };

  if (!form.loading && !form.isSupported) {
    return <UnsupportedState message={form.unsupportedMessage} />;
  }

  return (
    <VStack space="lg">
      <VStack space="xs">
        <Text size="xs" bold className="uppercase text-typography-500">
          País
        </Text>
        <Text bold className="text-typography-950">
          {countryName}
        </Text>
      </VStack>
      {form.loading ? (
        <Spinner />
      ) : (
        <TaxProfileFields
          form={form}
          countryCode={countryCode}
          perspective="payer"
          size="sm"
        />
      )}
      {acknowledgment ? (
        <FormInputBooleanCheckbox
          control={formProps.control}
          name="acknowledged"
          required
          size="sm"
          description={ACKNOWLEDGMENT_COPY}
        />
      ) : null}
      <LoadingButton
        action="brand"
        size="sm"
        rounded="lg"
        className="w-fit"
        loading={submitting}
        isDisabled={form.loading}
        onPress={() => void handleSubmit()}
      >
        {submitLabel}
      </LoadingButton>
    </VStack>
  );
}
